import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Grid,
  IconButton,
  Alert
} from '@mui/material';
import { format } from 'date-fns';
import { Plus, Trash, PencilSimple } from '@phosphor-icons/react';

function TimeOffTab({ timeOff = [], onAdd, onUpdate, onDelete, busy }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    startDate: '',
    endDate: '',
    reason: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);

  const resetForm = () => {
    setForm({ startDate: '', endDate: '', reason: '', description: '' });
    setEditingId(null);
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.startDate || !form.endDate) {
      setError('Start and end are required');
      return;
    }
    if (new Date(form.endDate) <= new Date(form.startDate)) {
      setError('End must be after start');
      return;
    }

    if (editingId) {
      await onUpdate(editingId, form, () => setDialogOpen(false));
    } else {
      await onAdd(form, () => setDialogOpen(false));
    }
    resetForm();
  };

  const sorted = [...timeOff].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  const formatRange = (entry) => {
    const start = new Date(entry.startDate);
    const end = new Date(entry.endDate);
    const sameDay = start.toDateString() === end.toDateString();
    const datePart = sameDay ? format(start, 'd MMM yyyy') : `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`;
    const timePart = sameDay ? `${format(start, 'HH:mm')}–${format(end, 'HH:mm')}` : '';
    return `${datePart}${timePart ? `, ${timePart}` : ''}`;
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6">Time off</Typography>
            <Typography variant="body2" color="text.secondary">
              Holidays, appointments, or one-off unavailability. These override normal hours.
            </Typography>
          </Box>
          <Button
            startIcon={<Plus size={18} />}
            variant="contained"
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
          >
            Add time off
          </Button>
        </Box>

        {sorted.length === 0 && (
          <Typography variant="body2" color="text.secondary">No time off recorded.</Typography>
        )}

        <Stack spacing={2}>
          {sorted.map((entry) => (
            <Card key={entry._id || `${entry.startDate}-${entry.endDate}`} variant="outlined">
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>{formatRange(entry)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {entry.reason || 'No reason provided'}
                    {entry.description ? ` • ${entry.description}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton color="primary" onClick={() => {
                    setEditingId(entry._id);
                    setForm({
                      startDate: entry.startDate ? entry.startDate.slice(0, 16) : '',
                      endDate: entry.endDate ? entry.endDate.slice(0, 16) : '',
                      reason: entry.reason || '',
                      description: entry.description || ''
                    });
                    setDialogOpen(true);
                  }} disabled={busy}>
                    <PencilSimple size={18} />
                  </IconButton>
                  <IconButton color="error" onClick={() => onDelete(entry)} disabled={busy}>
                    <Trash size={18} />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </CardContent>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          resetForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editingId ? 'Edit time off' : 'Add time off'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start"
                  type="datetime-local"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End"
                  type="datetime-local"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <TextField
              label="Reason"
              placeholder="Holiday, appointment, out of office"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
            <TextField
              label="Notes (optional)"
              multiline
              minRows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={busy}>
            {editingId ? 'Save changes' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default TimeOffTab;
